import type {
  DecayCandidate,
  DecayRow,
} from '../curriculum/_lib/decay-grouping'

const problem = (
  id: string,
  title: string,
  difficulty: DecayCandidate['difficulty'],
  est: number,
  doneCount: number,
  lastDone?: string,
): DecayCandidate => ({
  id,
  title,
  format: 'problem',
  difficulty,
  est,
  doneCount,
  ...(lastDone === undefined ? {} : { lastDone }),
})

const exercise = (
  id: string,
  title: string,
  difficulty: DecayCandidate['difficulty'],
  est: number,
  doneCount: number,
  lastDone?: string,
): DecayCandidate => ({
  id,
  title,
  format: 'exercise',
  difficulty,
  est,
  doneCount,
  ...(lastDone === undefined ? {} : { lastDone }),
})

const withStudyBoosters = (
  row: Omit<DecayRow, 'candidates'>,
  candidates: DecayCandidate[],
): DecayRow => ({
  ...row,
  candidates: [
    ...candidates,
    {
      id: `${row.id}-flashcard-deck`,
      title: `Flashcard deck — ${row.subtopic} (12 cards)`,
      format: 'flashcards',
      difficulty: '—',
      est: 0.25,
      doneCount: 2,
      lastDone: `${row.lastSeenWeeks} wks ago`,
    },
    {
      id: `${row.id}-mini-exercise`,
      title: `Mini-exercise — ${row.subtopic} drill`,
      format: 'exercise',
      difficulty: 'Easy',
      est: 0.5,
      doneCount: 0,
    },
  ],
})

export const MOCK_DECAY_ITEMS: DecayRow[] = [
  withStudyBoosters(
    {
      id: 'decay-binary-search-rotated-arrays',
      type: 'coding',
      topic: 'Binary search',
      subtopic: 'Rotated arrays',
      lastSeenWeeks: 12,
      risk: 82,
    },
    [
      problem(
        'decay-binary-search-rotated-arrays-search-in-rotated-sorted-array',
        'Search in Rotated Sorted Array',
        'Med',
        1,
        2,
        '12 wks ago',
      ),
      problem(
        'decay-binary-search-rotated-arrays-find-minimum-in-rotated-array',
        'Find Minimum in Rotated Array',
        'Med',
        1,
        1,
        '14 wks ago',
      ),
      problem(
        'decay-binary-search-rotated-arrays-first-bad-version',
        'First Bad Version',
        'Easy',
        0.5,
        3,
        '20 wks ago',
      ),
      problem(
        'decay-binary-search-rotated-arrays-search-a-2d-matrix',
        'Search a 2D Matrix',
        'Med',
        1,
        0,
      ),
      problem(
        'decay-binary-search-rotated-arrays-find-peak-element',
        'Find Peak Element',
        'Med',
        1,
        0,
      ),
      problem(
        'decay-binary-search-rotated-arrays-median-of-two-sorted-arrays',
        'Median of Two Sorted Arrays',
        'Hard',
        2,
        0,
      ),
    ],
  ),
  withStudyBoosters(
    {
      id: 'decay-binary-search-guess-and-check',
      type: 'coding',
      topic: 'Binary search',
      subtopic: 'Guess-and-check',
      lastSeenWeeks: 9,
      risk: 66,
    },
    [
      problem(
        'decay-binary-search-guess-and-check-koko-eating-bananas',
        'Koko Eating Bananas',
        'Med',
        1,
        1,
        '9 wks ago',
      ),
      problem(
        'decay-binary-search-guess-and-check-capacity-to-ship-packages',
        'Capacity to Ship Packages',
        'Med',
        1,
        0,
      ),
      problem(
        'decay-binary-search-guess-and-check-split-array-largest-sum',
        'Split Array Largest Sum',
        'Hard',
        2,
        0,
      ),
      problem(
        'decay-binary-search-guess-and-check-minimum-days-to-make-bouquets',
        'Minimum Days to Make Bouquets',
        'Med',
        1,
        0,
      ),
      problem(
        'decay-binary-search-guess-and-check-sqrt-x-drill',
        'Sqrt(x) drill',
        'Easy',
        0.5,
        2,
        '18 wks ago',
      ),
    ],
  ),
  withStudyBoosters(
    {
      id: 'decay-two-pointers-converging',
      type: 'coding',
      topic: 'Two pointers',
      subtopic: 'Converging',
      lastSeenWeeks: 10,
      risk: 74,
    },
    [
      problem(
        'decay-two-pointers-converging-container-with-most-water',
        'Container With Most Water',
        'Med',
        1,
        1,
        '10 wks ago',
      ),
      problem(
        'decay-two-pointers-converging-3sum',
        '3Sum',
        'Med',
        1.5,
        2,
        '11 wks ago',
      ),
      problem(
        'decay-two-pointers-converging-two-sum-ii',
        'Two Sum II',
        'Easy',
        0.5,
        1,
        '16 wks ago',
      ),
      problem(
        'decay-two-pointers-converging-sort-colors',
        'Sort Colors',
        'Med',
        1,
        0,
      ),
      problem(
        'decay-two-pointers-converging-trapping-rain-water',
        'Trapping Rain Water',
        'Hard',
        2,
        0,
      ),
    ],
  ),
  withStudyBoosters(
    {
      id: 'decay-graphs-bfs',
      type: 'coding',
      topic: 'Graphs',
      subtopic: 'BFS',
      lastSeenWeeks: 8,
      risk: 57,
    },
    [
      problem(
        'decay-graphs-bfs-number-of-islands',
        'Number of Islands',
        'Med',
        1,
        2,
        '8 wks ago',
      ),
      problem(
        'decay-graphs-bfs-rotting-oranges',
        'Rotting Oranges',
        'Med',
        1,
        1,
        '9 wks ago',
      ),
      problem('decay-graphs-bfs-01-matrix', '01 Matrix', 'Med', 1, 0),
      problem(
        'decay-graphs-bfs-clone-graph',
        'Clone Graph',
        'Med',
        1,
        1,
        '13 wks ago',
      ),
      problem('decay-graphs-bfs-word-ladder', 'Word Ladder', 'Hard', 2, 0),
    ],
  ),
  withStudyBoosters(
    {
      id: 'decay-caching-consistent-hashing',
      type: 'sd',
      topic: 'Caching',
      subtopic: 'Consistent hashing',
      lastSeenWeeks: 8,
      risk: 61,
    },
    [
      problem(
        'decay-caching-consistent-hashing-design-consistent-hashing',
        'Design Consistent Hashing',
        'Med',
        1.5,
        1,
        '8 wks ago',
      ),
      problem(
        'decay-caching-consistent-hashing-design-a-distributed-cache',
        'Design a Distributed Cache',
        'Med',
        2,
        0,
      ),
      problem(
        'decay-caching-consistent-hashing-design-cdn-request-routing',
        'Design CDN request routing',
        'Med',
        1.5,
        0,
      ),
      problem(
        'decay-caching-consistent-hashing-redis-internals-reading',
        'Redis internals — reading',
        'Med',
        1,
        0,
      ),
      problem(
        'decay-caching-consistent-hashing-hot-key-mitigation-drill',
        'Hot-key mitigation drill',
        'Hard',
        1.5,
        0,
      ),
    ],
  ),
  withStudyBoosters(
    {
      id: 'decay-classics-fan-out',
      type: 'sd',
      topic: 'Classics',
      subtopic: 'Fan-out',
      lastSeenWeeks: 11,
      risk: 58,
    },
    [
      problem(
        'decay-classics-fan-out-design-a-news-feed',
        'Design a News Feed',
        'Med',
        2,
        1,
        '11 wks ago',
      ),
      problem(
        'decay-classics-fan-out-design-twitter-timeline',
        'Design Twitter timeline',
        'Med',
        2,
        0,
      ),
      problem(
        'decay-classics-fan-out-design-a-notification-service',
        'Design a Notification Service',
        'Med',
        2,
        0,
      ),
      problem(
        'decay-classics-fan-out-on-write-vs-read-reading',
        'Fan-out on write vs read — reading',
        'Easy',
        0.5,
        0,
      ),
    ],
  ),
  withStudyBoosters(
    {
      id: 'decay-conflict-manager',
      type: 'behavioral',
      topic: 'Conflict',
      subtopic: 'Manager',
      lastSeenWeeks: 9,
      risk: 52,
    },
    [
      problem(
        'decay-conflict-manager-tell-me-about-a-recent-conflict',
        'Tell Me About a Recent Conflict',
        'Med',
        1,
        1,
        '9 wks ago',
      ),
      problem(
        'decay-conflict-manager-disagree-with-your-manager',
        'Disagree with your manager',
        'Med',
        1,
        0,
      ),
      problem(
        'decay-conflict-manager-pushback-you-regret',
        'Pushback you regret',
        'Med',
        1,
        0,
      ),
      exercise(
        'decay-conflict-manager-re-record-conflict-story-timed',
        'Re-record conflict story (timed)',
        'Easy',
        0.5,
        2,
        '10 wks ago',
      ),
    ],
  ),
]
